import { useEffect, useMemo, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { DynamicFormRenderer } from '@/components/forms/dynamic-form-renderer';
import { buildDynamicZodSchema, getDefaultValueForField } from '@/lib/dynamic-form-schema';
import type {
  OnboardingFormConfig,
  FormField,
  FormAnswerValue,
  OnboardingAnswerEntry,
} from '@/types';
import {
  getFormValueFromStoredOnboarding,
  normalizeOnboardingDateStorageValue,
  sanitizeOnboardingAnswers,
} from '@/lib/onboarding-answers';

interface FormRendererProps {
  config: OnboardingFormConfig;
  onSubmit: (answers: Record<string, FormAnswerValue>) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  initialAnswers?: Record<string, FormAnswerValue | OnboardingAnswerEntry>;
  submitText?: string;
  onDirtyChange?: (dirty: boolean) => void;
}

export function FormRenderer({
  config,
  onSubmit,
  onCancel,
  isSubmitting = false,
  initialAnswers = {},
  submitText = 'Finish & Join',
  onDirtyChange,
}: FormRendererProps) {
  const steps = useMemo(() => config.steps || [], [config.steps]);
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [uploading, setUploading] = useState(false);

  // Flatten all fields across all steps into a single zod schema so that the
  // user can navigate back-and-forth without losing state.
  const allFields = useMemo<FormField[]>(
    () => steps.flatMap((s) => (Array.isArray(s.fields) ? s.fields : [])),
    [steps]
  );

  const schema = useMemo(() => buildDynamicZodSchema(allFields), [allFields]);

  // Seed every field with a type-appropriate empty default so that inputs are
  // always controlled from first render — avoids React's
  // "changing an uncontrolled input to be controlled" warning and keeps RHF's
  // dirty-tracking sane.
  const defaults = useMemo(
    () =>
      Object.fromEntries(
        allFields.map((f) => {
          const provided = getFormValueFromStoredOnboarding(initialAnswers[f.id]);
          if (provided !== undefined && provided !== null) {
            if (f.type === 'DATE' && typeof provided === 'string') {
              return [f.id, normalizeOnboardingDateStorageValue(provided)];
            }
            return [f.id, provided];
          }
          return [f.id, getDefaultValueForField(f)];
        })
      ),
    // initialAnswers is intentionally a stable snapshot — we only re-seed when
    // the schema (fields) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allFields]
  );

  const form = useForm<Record<string, FormAnswerValue>>({
    resolver: zodResolver(schema) as unknown as Resolver<Record<string, FormAnswerValue>>,
    defaultValues: defaults,
    mode: 'onSubmit',
  });

  const isDirty = form.formState.isDirty;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const currentStepData = steps[currentStep];
  const currentFields = currentStepData?.fields || [];
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = async () => {
    const currentIds = currentFields.map((f) => f.id);
    const valid = await form.trigger(currentIds as never);
    if (!valid) {
      // Bubble up the first error as a toast so users who scrolled past the
      // failing field still see why nothing happened.
      const firstError = Object.values(form.formState.errors)[0] as
        | { message?: string }
        | undefined;
      if (firstError?.message) toast.error(firstError.message);
      return;
    }

    if (!isLastStep) {
      setDirection(1);
      setCurrentStep((prev) => prev + 1);
    } else {
      const values = form.getValues();
      await onSubmit(sanitizeOnboardingAnswers(values));
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep((prev) => prev - 1);
    } else {
      onCancel();
    }
  };

  if (steps.length === 0) return null;

  return (
    <div className="w-full max-w-lg mx-auto">
      <div className="mb-6 flex items-center justify-between gap-4">
        {steps.length > 1 && (
          <div className="flex gap-1">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 w-6 rounded-full transition-colors ${
                  idx <= currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        )}
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          {currentStepData.title}
        </span>
      </div>

      <Form {...form}>
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            initial={{ x: direction * 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: direction * -20, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <DynamicFormRenderer
              fields={currentFields}
              control={form.control}
              uploadFolder="onboarding-answers"
              onUploadingChange={setUploading}
            />
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex justify-between pt-4 border-t border-border">
          <Button type="button" variant="ghost" onClick={handleBack} disabled={isSubmitting}>
            {currentStep === 0 ? (
              'Cancel'
            ) : (
              <>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </>
            )}
          </Button>
          <Button type="button" onClick={handleNext} disabled={isSubmitting || uploading}>
            {(isSubmitting || uploading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {uploading ? (
              'Wait for upload…'
            ) : isLastStep ? (
              submitText
            ) : (
              <>
                <span className="mr-2">Next</span> <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}
